import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { THEME, gradients, shadows } from '../constants/theme';

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('information-we-collect');

  const sections = [
    { id: 'information-we-collect', title: 'Information We Collect', icon: '📊' },
    { id: 'how-we-use', title: 'How We Use Your Information', icon: '🔄' },
    { id: 'how-we-share', title: 'How We Share Your Information', icon: '🤝' },
    { id: 'data-security', title: 'Data Security', icon: '🔒' },
    { id: 'data-retention', title: 'Data Retention', icon: '⏳' },
    { id: 'your-rights', title: 'Your Rights and Choices', icon: '✅' },
    { id: 'international', title: 'International Data Transfers', icon: '🌍' },
    { id: 'children', title: 'Children\'s Privacy', icon: '👶' },
    { id: 'changes', title: 'Changes to Privacy Policy', icon: '📝' },
    { id: 'contact', title: 'Contact Us', icon: '📧' },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: THEME.background }}>
      {/* Fixed Sidebar */}
      <div className="fixed top-0 left-0 h-full w-64 overflow-y-auto"
        style={{ backgroundColor: THEME.cardbg, boxShadow: shadows.lg }}>
        <div className="p-6" style={{ background: gradients.primary }}>
          <Link to="/" className="flex items-center space-x-2">
            <img src="/gradient_logo.png" alt="Aided" className="h-8 w-8" />
            <span className="text-xl font-bold text-white">Aided</span>
          </Link>
        </div>
        <nav className="p-4">
          <div className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`
                  w-full text-left px-4 py-2 rounded-lg text-sm font-medium
                  transition-colors duration-150 ease-in-out
                  flex items-center space-x-2
                `}
                style={{
                  backgroundColor: activeSection === section.id ? THEME.primaryLight + '20' : 'transparent',
                  color: activeSection === section.id ? THEME.primary : THEME.text
                }}
              >
                <span className="text-lg">{section.icon}</span>
                <ChevronRightIcon 
                  className={`h-4 w-4 transition-transform duration-200 ${
                    activeSection === section.id ? 'transform rotate-90' : ''
                  }`}
                />
                <span>{section.title}</span>
              </button>
            ))}
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="pl-64">
        <div className="max-w-4xl mx-auto px-8 py-12">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-4xl font-bold mb-4" style={{ color: THEME.text }}>Privacy Policy</h1>
            <div className="flex items-center space-x-4 text-sm" style={{ color: THEME.subtext }}>
              <span>Effective Date: 2025-01-16</span>
              <span>•</span>
              <span>Last Updated: 2025-01-16</span>
            </div>
          </div>

          {/* Introduction */}
          <div className="prose prose-lg mb-12" style={{ color: THEME.text }}>
            <p>
              At Aided, we are committed to protecting your privacy and ensuring the security of your personal and business information. 
              This Privacy Policy describes how we collect, use, disclose, and safeguard your information when you use our website, 
              mobile application, and services (collectively, the "Platform"). Please read this policy carefully to understand our 
              practices regarding your information and how we will treat it.
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-16">
            <section id="information-we-collect" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">📊</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Information We Collect</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <h3 className="text-xl font-semibold mb-4">Personal Information</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Name, email address, phone number, and payment information</li>
                  <li>Account creation and authentication details</li>
                  <li>Support requests and feedback</li>
                  <li>Two-factor authentication setup information</li>
                </ul>

                <h3 className="text-xl font-semibold mb-4 mt-8">Business Information</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Business name, address, tax ID, and industry</li>
                  <li>Logo and branding assets</li>
                  <li>Currency preferences and payment terms</li>
                  <li>Google Maps integration data for address validation</li>
                </ul>

                <h3 className="text-xl font-semibold mb-4 mt-8">Usage Information</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Device information (IP address, browser type, operating system)</li>
                  <li>Log data (access times, pages viewed, features used)</li>
                  <li>Location information based on IP address or precise location (if enabled)</li>
                </ul>

                <h3 className="text-xl font-semibold mb-4 mt-8">Content</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Client details and communications</li>
                  <li>Quotes, invoices, and receipts</li>
                  <li>Service desk tickets and related communications</li>
                </ul>
              </div>
            </section>

            <section id="how-we-use" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">🔄</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>How We Use Your Information</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <h3 className="text-xl font-semibold mb-4">Platform Operation</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Account creation and management</li>
                  <li>Feature delivery and personalization</li>
                  <li>AI-powered features (response suggestions, quote conversion analysis)</li>
                  <li>Platform improvement and development</li>
                </ul>

                <h3 className="text-xl font-semibold mb-4 mt-8">Communication</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Important updates and notifications</li>
                  <li>Customer support responses</li>
                  <li>Marketing communications (with opt-in consent)</li>
                </ul>

                <h3 className="text-xl font-semibold mb-4 mt-8">Security and Compliance</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Account protection and fraud prevention</li>
                  <li>Legal compliance and regulatory requirements</li>
                  <li>Terms of Service enforcement</li>
                </ul>
              </div>
            </section>

            <section id="how-we-share" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">🤝</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>How We Share Your Information</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <h3 className="text-xl font-semibold mb-4">Service Providers</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Payment processors (Stripe, PayPal)</li>
                  <li>Cloud hosting (Supabase)</li>
                  <li>Email services (SendGrid, Mailchimp)</li>
                  <li>Analytics (Google Analytics)</li>
                  <li>Mapping services (Google Maps)</li>
                </ul>

                <h3 className="text-xl font-semibold mb-4 mt-8">Client Portal Access</h3>
                <p>
                  When you use our Client Portal feature, certain information like quotes, invoices, 
                  payment history, and communications will be accessible to your clients.
                </p>

                <h3 className="text-xl font-semibold mb-4 mt-8">Legal Requirements</h3>
                <p>
                  We may disclose information in response to legal requests, court orders, or to comply 
                  with applicable laws and regulations in Australia.
                </p>
              </div>
            </section>

            <section id="data-security" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">🔒</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Data Security</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Encryption of sensitive data in transit and at rest</li>
                  <li>Restricted access controls for authorized personnel</li>
                  <li>Row-level security through Supabase</li>
                  <li>Two-factor authentication support</li>
                  <li>Regular security audits and vulnerability assessments</li>
                  <li>PCI compliance for payment processing</li>
                </ul>
              </div>
            </section>

            <section id="data-retention" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">⏳</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Data Retention</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <p>
                  We retain your information for as long as necessary to provide our services and comply with 
                  legal obligations. This includes retention periods required by Australian tax, accounting, 
                  and other legal requirements.
                </p>
              </div>
            </section>

            <section id="your-rights" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">✅</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Your Rights and Choices</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access and update your personal information</li>
                  <li>Opt-out of marketing communications</li>
                  <li>Request account deactivation</li>
                  <li>Request data deletion (subject to legal requirements)</li>
                  <li>Lodge complaints with the Office of the Australian Information Commissioner (OAIC)</li>
                </ul>
              </div>
            </section>

            <section id="international" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">🌍</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>International Data Transfers</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <p>
                  While we primarily store data in Australia, we may transfer data to service providers in other countries. 
                  We ensure appropriate safeguards are in place and comply with Australian Privacy Principles for 
                  international data transfers.
                </p>
              </div>
            </section>

            <section id="children" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">👶</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Children's Privacy</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <p>
                  Our Platform is not intended for children under 18. We do not knowingly collect personal 
                  information from children under 18. If you believe we have collected such information, 
                  please contact us to have it removed.
                </p>
              </div>
            </section>

            <section id="changes" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">📝</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Changes to Privacy Policy</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <p>
                  We may update this Privacy Policy periodically. We will notify you of material changes by posting 
                  the updated policy and updating the "Last Updated" date. Continued use of the Platform after 
                  changes constitutes acceptance of the updated policy.
                </p>
              </div>
            </section>

            <section id="contact" className="scroll-mt-20">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: THEME.primaryLight + '20' }}>
                  <span className="text-2xl">📧</span>
                </div>
                <h2 className="text-3xl font-bold" style={{ color: THEME.text }}>Contact Us</h2>
              </div>
              <div className="prose prose-lg" style={{ color: THEME.text }}>
                <p>
                  For questions about this Privacy Policy or to make a complaint, contact us at:{' '}
                  <a href="mailto:support@aided.com" 
                    className="font-medium hover:underline"
                    style={{ color: THEME.primary }}>
                    support@aided.com
                  </a>
                </p>
                <p className="mt-4">
                  You can also contact the Office of the Australian Information Commissioner (OAIC):{' '}
                  <a href="https://www.oaic.gov.au" 
                    className="font-medium hover:underline"
                    style={{ color: THEME.primary }}
                    target="_blank"
                    rel="noopener noreferrer">
                    www.oaic.gov.au
                  </a>
                  {' '}or call 1300 363 992.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 